import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarDays } from "lucide-react";
import { api, price } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Hero } from "@/components/Hero";
import { UpcomingDates } from "@/components/UpcomingDates";

const FeaturedHero = ({ ev }) => <Hero ev={ev} ctaHref={`/events/${ev.slug}#book`} ctaTestId="hero-reserve-button" testid="featured-hero" />;

const EventRow = ({ ev, i }) => {
  const cities = [...new Set(ev.nights.map((n) => n.city).filter(Boolean))];
  const where = cities.length > 1 ? `${cities.length} cities` : cities[0] || ev.location || "Kentucky";
  return (
    <Link to={`/events/${ev.slug}`} data-testid={`event-card-${ev.slug}`}
      className="reveal group flex flex-col overflow-hidden rounded-lg border border-[#E5DACA] bg-white transition-shadow duration-300 hover:shadow-[0_12px_30px_-12px_rgba(60,40,10,0.25)]"
      style={{ animationDelay: `${i * 0.08}s` }}>
      {ev.image && <img src={ev.image} alt="" className="aspect-[16/9] w-full object-cover" />}
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        {ev.coming_soon && <span className="mb-2 self-start rounded-full border border-dashed border-[#C9A24B] px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#A8832F]">Coming soon</span>}
        <h3 className="font-display text-2xl text-[#1F1B17] transition-colors group-hover:text-[#8C6A22]">{ev.title}</h3>
        <p className="mt-2 line-clamp-2 text-sm text-[#6B6157]">{ev.tagline}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#6B6157]">
          <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#C9A24B]" />{where}</span>
          <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#C9A24B]" />{ev.nights.length} {ev.nights.length === 1 ? "night" : "nights"}</span>
          {!ev.coming_soon && ev.price_from != null && <span className="font-bold text-[#A8832F]">from {price(ev.price_from)}</span>}
        </div>
        <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#1F1B17]">Details <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
      </div>
    </Link>
  );
};

export default function Home() {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);
  const featured = events?.find((e) => e.featured) || events?.[0];
  return (
    <main data-testid="home-page">
      {!events && <Skeleton className="h-[520px] w-full rounded-none" />}
      {featured && <FeaturedHero ev={featured} />}
      <section className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-20" data-testid="events-list">
        <p className="eyebrow">On the calendar</p>
        <h2 className="mb-8 mt-3 font-display text-3xl text-[#1F1B17] sm:text-4xl">Bourbon evenings worth the trip</h2>
        {events && <UpcomingDates events={events} />}
      </section>
      {events?.length > 0 && (
        <section className="bg-[#F1E9DA]" data-testid="experiences-list">
          <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
            <h2 className="font-display text-3xl text-[#1F1B17] sm:text-4xl">The experiences</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {events.map((ev, i) => <EventRow key={ev.id} ev={ev} i={i} />)}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
